import { useNavigate } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import { cn } from '~/utils/cn';

interface Props {
  /** Localized feature label the copy refers to (e.g. "Gap Analysis",
   *  "Search Descriptors") — so the banner names the tab it's shown on. */
  feature: string;
  className?: string;
}

/**
 * Prompt shown when Google Ads isn't connected — competition / search-volume
 * data (e.g. the Gap Analysis Competition column) needs a Google Ads link.
 * Self-contained: renders nothing when Google Ads is already connected, and
 * stays hidden while the connectors list is still resolving so a slow response
 * can't flash a "not connected" prompt at a user who is connected.
 */
export function GoogleAdsConnectBanner({ feature, className }: Props) {
  const { t } = useTranslation('catalog-operations');
  const navigate = useNavigate();
  const { data: connectedConnectors } = useConnectedConnectors();

  // Treat as connected until the list resolves — see the no-flash note above.
  const isGoogleAdsConnected = connectedConnectors
    ? connectedConnectors.some((c) => c.code === 'GOOGLE_ADS')
    : true;
  if (isGoogleAdsConnected) return null;

  return (
    <div
      data-testid="google-ads-connect-banner"
      className={cn(
        'bg-info-50 flex flex-col gap-3 rounded-xl px-5 py-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <Info aria-hidden="true" className="text-info mt-0.5 h-5 w-5 shrink-0" />
        <p className="text-foreground text-sm leading-5">
          {t('googleAdsBanner.message', { feature })}
        </p>
      </div>
      <Button
        data-testid="google-ads-connect-banner-connect-button"
        size="sm"
        className="shrink-0 cursor-pointer"
        onClick={() => {
          void navigate({ to: '/onboarding/integration/google/google-ads' });
        }}
      >
        {t('googleAdsBanner.cta')}
      </Button>
    </div>
  );
}
